/**
 * `BreadcrumbList` JSON-LD, so a crawler can see that `/refuge/faq` sits under
 * `/refuge` rather than guessing hierarchy from the URL.
 *
 * Emitted from `layouts/default.vue`, once, for every page that uses the
 * layout. There is no visible breadcrumb in the UI — Google accepts the markup
 * on its own, and adding a trail to every interior page is a design decision
 * nobody asked for.
 *
 * `breadcrumbTrailFor()` is pure so `tests/structuredData.test.ts` can check
 * that every route under `pages/` is labelled, the way
 * `tests/searchIndex.test.ts` checks every route is searchable.
 */

/**
 * Route path to the i18n key naming it. Every key here already existed for the
 * nav or a page heading — a breadcrumb label is the same words.
 *
 * Keyed by path rather than by route base name because the parents are derived
 * by trimming path segments, and a path is what `localePath()` wants back.
 */
export const BREADCRUMB_LABEL_KEYS: Record<string, string> = {
    '/': 'Home',

    '/refuge': 'The Refuge',
    '/refuge/story': 'Our Story',
    '/refuge/booking': 'Booking',
    '/refuge/approach': 'How to get here',
    '/refuge/faq': 'faqTitle',
    '/refuge/cameras': 'liveWebCams',
    '/refuge/photos': 'Photos',
    '/refuge/videos': 'Videos',

    '/services': 'Services',
    '/services/accommodation': 'Accommodation',
    '/services/menu': 'ourMenu',

    '/activities': 'Activities',
    '/activities/hiking-trails': 'hikingTrails',
    '/activities/alpine': 'Alpine',
};

export interface BreadcrumbStep {
    /** Unprefixed route path; `localePath()` adds the locale. */
    path: string;
    /** i18n key for the label. */
    labelKey: string;
}

/**
 * The trail from Home down to `path`, built by walking the path's own
 * segments — `/refuge/faq` yields Home, The Refuge, Frequently Asked Questions.
 *
 * Returns nothing for the home page (a one-item breadcrumb says nothing) and
 * for any path not in the map, which is how `/dev/test` stays out.
 */
export function breadcrumbTrailFor(path: string): BreadcrumbStep[] {
    // Tolerate a trailing slash; `/refuge/` and `/refuge` are the same page.
    const normalized = path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;

    if (normalized === '/' || !(normalized in BREADCRUMB_LABEL_KEYS)) return [];

    const segments = normalized.split('/').filter(Boolean);
    const trail: BreadcrumbStep[] = [{ path: '/', labelKey: BREADCRUMB_LABEL_KEYS['/']! }];

    let walked = '';
    for (const segment of segments) {
        walked += `/${segment}`;
        const labelKey = BREADCRUMB_LABEL_KEYS[walked];
        // A missing intermediate is skipped rather than fatal: the trail stays
        // useful, and the test below catches the gap at build time.
        if (labelKey) trail.push({ path: walked, labelKey });
    }

    return trail;
}

/**
 * `/de/refuge/faq` -> `/refuge/faq`. The strategy is `prefix_except_default`,
 * so the Greek paths arrive unprefixed and pass through untouched.
 *
 * The codes come from the i18n config rather than being listed here, so adding
 * a sixth locale does not quietly stop breadcrumbs working on it.
 */
export function stripLocalePrefix(path: string, codes: readonly string[]): string {
    const [, first] = path.split('/');
    if (first && codes.includes(first)) {
        return path.slice(first.length + 1) || '/';
    }
    return path;
}

/**
 * Registers the breadcrumb for the current route.
 *
 * A computed, like the FAQ and menu graphs: the labels are translated and
 * switching locale keeps the same component mounted.
 */
export function useBreadcrumbSchema() {
    const route = useRoute();
    const localePath = useLocalePath();
    const { t, locales } = useI18n();

    const codes = computed(() => locales.value.map(entry => (
        typeof entry === 'string' ? entry : entry.code
    )));

    useSchemaOrg(computed(() => {
        const trail = breadcrumbTrailFor(stripLocalePrefix(route.path, codes.value));
        if (!trail.length) return [];

        return [
            defineBreadcrumb({
                itemListElement: trail.map(step => ({
                    name: t(step.labelKey),
                    item: localePath(step.path),
                })),
            }),
        ];
    }));
}
